import { FIRST_LOAD_STATE_WAIT_MS } from 'projects/service/src/app/shared/utils/grid-persistence';
import { ActionIconDef, GridConfig, GridFilter, ToolbarActionDef } from '@inavista/components/grid';
import { REMOTE_SEARCH_ROW_KEY } from '../../utils/remote-search-grid-api';
import { centreStatusColumns } from 'projects/service/src/app/shared/utils/grid-status-align';

// Item advanced search (Service Order and ISC Execution tabs, assignment popups, return / core return orders,
// shipments, parts orders, inventory adjustments, ISC claims) on @inavista/components. Same columns, labels, order,
// widths, alignment, links, status pill, On Hand checkbox and toolbar (Search, Clear filters) as the Syncfusion grid
// it replaces. Rows come from the same endpoints with the same request (RemoteSearchGridApi); 50 rows per request,
// loaded as the user scrolls. No paging (Martin).

export const ITEMS_ADVANCED_SEARCH_PERSISTENCE_KEY = 'rims.service.items-advanced-search';
export const ITEMS_ADVANCED_SEARCH_URL = 'rims-search://items-advanced-search';
export const PROCEED_ACTION = 'proceed';
export const ON_HAND_TOGGLE = 'onHand';
// The toggle's value reaches RemoteSearchGridApi as this parameter (never sent to the endpoint as such).
export const ON_HAND_PARAM = 'onHandOnly';

// ServiceItemSearchViewModel, in the old grid's column order. On Hand (int) is only for non-ISC users, before
// Item Description.
export function itemsAdvancedSearchColumns(showOnHand: boolean): [string, string][] {
  return [
    ['company', 'nvarchar'],
    ['item', 'nvarchar'],
    ...(showOnHand ? [['onHand', 'int'] as [string, string]] : []),
    ['itemDescription', 'nvarchar'],
    ['priceUnit', 'nvarchar'],
    ['serviceItemGroup', 'nvarchar'],
    ['status', 'nvarchar'],
    ['alternateItem', 'nvarchar'],
    ['costComponent', 'nvarchar'],
    ['itemGroup', 'nvarchar'],
  ];
}

// The On Hand checkbox: the items in stock.
// Fixed (Martin, 2026-10-29): the old filter was text, onHand 'doesnotcontain' '0', so it also hid every item with
// 10, 20, 30, 100... on hand. Now on hand > 0 (same endpoint, same onWhere, another operator).
export const onHandFilter = (value: string): GridFilter | null =>
  value === 'true' ? { column: 'onHand', operator: 'greaterThan', values: ['0'] } : null;

const ARROW_RIGHT_ICON = '<line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />';
const ON_HAND_ICON = '<path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />';

// The old command column: one "Proceed" arrow, frozen right, 120px.
export const ITEMS_PROCEED_ACTION_ICONS: ActionIconDef[] = [
  { key: PROCEED_ACTION, label: 'Proceed', icon: ARROW_RIGHT_ICON, colorFn: () => '#000000de' },
];

export function buildItemsAdvancedSearchGridConfig(showOnHand: boolean, statusColor: (value: unknown) => string): GridConfig {
  const order = itemsAdvancedSearchColumns(showOnHand).map(([c]) => c);
  // The old fixed widths; the others had none and shared the rest, so they start equal and stretch.
  const fixed: Record<string, number> = { company: 120, status: 120 };
  const toolbarActions: ToolbarActionDef[] = showOnHand
    ? [{ key: ON_HAND_TOGGLE, label: 'On Hand', icon: ON_HAND_ICON, kind: 'toggle', queryParam: ON_HAND_PARAM }]
    : [];
  return centreStatusColumns({
    apiBaseUrl: ITEMS_ADVANCED_SEARCH_URL,
    keyColumn: REMOTE_SEARCH_ROW_KEY,
    // The old page size.
    pageSize: 50,
    rowHeight: 30,
    defaultColumnOrder: order,
    // Price Unit was hidden, with no column chooser to show it; the row key is ours. Company is not shown (Martin,
    // 2026-10-29: one company per tenant, so it never told the user anything); it stays in the picked row.
    hiddenByDefaultColumns: ['company', 'priceUnit', REMOTE_SEARCH_ROW_KEY],
    columnLabels: {
      company: 'Company', item: 'Item', onHand: 'On Hand', itemDescription: 'Item Description', priceUnit: 'Price Unit',
      serviceItemGroup: 'Service Item Group', status: 'Status', alternateItem: 'Alternate Item',
      costComponent: 'Cost Component', itemGroup: 'Item Group',
    },
    // Every column left, as the old grid.
    columnAlign: Object.fromEntries(order.map(c => [c, { horizontal: 'left' as const, header: { horizontal: 'left' as const } }])),
    columnWidths: Object.fromEntries(order.map(c => [c, fixed[c] ?? 150])),
    stretchColumns: true,
    columnSizing: { company: { noStretch: true }, status: { noStretch: true } },
    // On Hand had format 'n': whole number with thousands separators.
    columnNumberFormats: { onHand: { maximumFractionDigits: 0 } },
    // Status as the old pill (getStatusClass).
    columnColors: { status: statusColor },
    // Item picks the row (as the Proceed arrow); Item Description opens the Item Information popup.
    hyperlinkColumns: ['item', 'itemDescription'],
    // Item, Service Item Group, Status and Item Group had the Excel (checkbox) filter; the rest the Menu filter.
    excelFilterColumns: ['item', 'serviceItemGroup', 'status', 'itemGroup'],
    // Fixed (Martin, 2026-10-28): the old sortSettings said direction 'Desending' (typo); RIMS API sorts every
    // direction other than 'ascending' DESC, so it was On Hand descending, as here. For ISC users the grid had no
    // On Hand column and Syncfusion dropped the sort: the endpoint's own order (item) applies, as here.
    ...(showOnHand ? { defaultSort: { column: 'onHand', direction: 'desc' as const } } : {}),
    // Ctrl+click adds a sort column, as the old grid (Syncfusion multi-sort).
    multiSort: true,
    // The old actions column was 120px wide.
    actionsColumnMinWidth: 120,
    searchHint: 'Searches every column.',
    // The old toolbar: On Hand (not for ISC users), Search and Clear all Filters.
    toolbarActions,
    enableColumnsPicker: false,
    enableExport: false,
    enableSavedViews: false,
    enableScrollButtons: false,
    enableFitColumns: false,
    enableRefresh: false,
    // Reset layout undoes the layout the grid now remembers (as on the other Service Order popup grids).
    enableResetLayout: true,
    // The footer gives the old "Total Records" count.
    enableFooter: true,
    loadingIndicator: 'banner',
    emptyStateMessage: 'No items to display.',
    // Right-click a cell filters by its value (Martin, 2026-10-29: every grid that filters).
    enableContextMenuFilter: true,
    persistenceKey: ITEMS_ADVANCED_SEARCH_PERSISTENCE_KEY,
    firstLoadStateWaitMs: FIRST_LOAD_STATE_WAIT_MS,
  });
}
